import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AuthState, JWTPayload } from '../types';
import { jwtDecode } from 'jwt-decode';
import { logger } from '../utils/logger';
import { useChatStore } from './chatStore';

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,

      login: (tokens: { accessToken: string; refreshToken: string }) => {
        try {
          const decoded = jwtDecode<JWTPayload>(tokens.accessToken);

          // Validate token expiration
          if (decoded.exp && decoded.exp * 1000 < Date.now()) {
            logger.warn('Token has expired', { exp: decoded.exp });
            throw new Error('Token has expired');
          }

          // Validate required claims
          if (!decoded.userId || !decoded.tenantId || !decoded.email) {
            logger.error('Invalid token: missing required claims', { decoded });
            throw new Error('Invalid token: missing required claims');
          }

          set({
            token: tokens.accessToken,
            refreshToken: tokens.refreshToken,
            user: decoded,
            isAuthenticated: true,
          });

          logger.info('User logged in successfully', { userId: decoded.userId });
        } catch (error) {
          logger.error('Token validation failed', error);
          // Clear any existing auth state
          set({
            token: null,
            refreshToken: null,
            user: null,
            isAuthenticated: false,
          });
          throw error; // Re-throw to handle in UI
        }
      },

      logout: () => {
        logger.info('User logged out');
        // Clear chat store state to prevent stale data on next login
        useChatStore.getState().clearState();
        set({
          token: null,
          refreshToken: null,
          user: null,
          isAuthenticated: false,
        });
        localStorage.removeItem('oquis-auth-storage');
        // Cleanup legacy keys (migration support)
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        // Note: We keep user-specific workspace/session data (oquis-{userId}-*) 
        // so users can restore their last session on next login
      },

      setToken: (token: string) => {
        set({ token });
      },
    }),
    {
      name: 'oquis-auth-storage',
    }
  )
);
